import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  BadgeIndianRupee,
  HeartHandshake,
  PhoneCall,
  ShieldAlert,
  UserRound,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useLanguage } from "../../context/LanguageContext";

import BottomNavbar from "../../components/BottomNavbar";
import Navbar from "../../components/Gnavbar";
import FullPageLoader from "../../components/FullPageLoader";

import { RootState } from "../../store";
import { isAccountSuspended } from "../../utils/bureau";
import { ProfileCard } from "../../types/profile";

import WalletPanel from "../../components/WalletPanel";
import BusinessScore from "./Components/BusinessScore";
import DashboardStatCard from "./Components/DashboardStatCard";
import DashboardSection from "./Components/DashboardSection";
import DirectRegisteredProfiles from "./Components/DirectRegisteredProfiles";

import {
  DashboardGroupedSection,
  GenderTabValue,
  ProfileSectionConfig,
} from "./dashboard.types";

import { parseNumericValue } from "./dashboard.utils";

import {
  DEFAULT_PROFILE_SECTIONS,
  SECTION_BADGES,
  WEBSITE_ROUTES,
} from "./dashboard.constants";

import useDashboardData from "./hooks/useDashboardData";
import useDashboardScroll from "./hooks/useDashboardScroll";
import useProfileSections from "./hooks/useProfileSections";
import usePaymentStatus from "./hooks/usePaymentStatus";
import useBusinessScore from "./hooks/useBusinessScore";
import useDirectRegisteredProfiles from "./hooks/useSuperAdminProfiles";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

const INITIAL_SECTION_TABS: Record<string, GenderTabValue> = {
  "new-profiles": "male",
  "incomplete-profiles": "male",
  "other-bureau-profiles": "male",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const mobileNavSpacerRef = useRef<HTMLDivElement | null>(null);
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const dashboardLoaded = useSelector(
    (state: RootState) => state.dashboard.loaded,
  );
  const isBureauSuspended = isAccountSuspended(bureauInfo?.suspend);

  const [sectionTabs, setSectionTabs] =
    useState<Record<string, GenderTabValue>>(INITIAL_SECTION_TABS);

  const [selectedCategory, setSelectedCategory] =
    useState<string>("new-profiles");

  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);

  /*
   * Profile sections
   */
  const { profileSections, setProfileSections, applyProfileSections } =
    useProfileSections({
      defaultSections: DEFAULT_PROFILE_SECTIONS,
    });

  /*
   * Dashboard API
   */
  const {
    dashboardData,
    dashboardLoading,
    dashboardError,
    fetchDashboardData,
    refreshDashboard,
  } = useDashboardData({
    onPayloadLoaded: applyProfileSections,
  });

  const showInitialDashboardLoader =
    dashboardLoading && !dashboardLoaded && !dashboardError;

  useEffect(() => {
    if (showInitialDashboardLoader) {
      return;
    }

    const navEl = document.getElementById("dashboard-mobile-navbar");
    const spacerEl = mobileNavSpacerRef.current;

    if (!navEl || !spacerEl || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateHeight = () => {
      spacerEl.style.setProperty(
        "--dashboard-nav-h",
        `${navEl.offsetHeight}px`,
      );
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(navEl);

    return () => observer.disconnect();
  }, [showInitialDashboardLoader]);

  /*
   * Dashboard scroll restoration
   */
  const { persistDashboardScrollState } = useDashboardScroll({
    loading: dashboardLoading,
    dependency: profileSections,
    selectedCategory,
    setSelectedCategory,
  });

  /*
   * Payment-status actions
   */
  const { renderPaymentActions } = usePaymentStatus({
    profileSections,
    setProfileSections,
    fetchDashboardData,
  });

  /*
   * Business-score calculations
   */
  const businessScore = useBusinessScore({
    profileSections,
    dashboardData,
    dashboardLoading,
  });

  /*
   * Direct registered profiles
   */
  const {
    profiles: superAdminProfiles,
    totalCount: superAdminProfilesTotal,
    scrollerRef: superAdminScrollerRef,
    openWhatsAppForProfile: openWhatsAppForSuperAdminProfile,
  } = useDirectRegisteredProfiles({
    dashboardData,
    bureauInfo,
  });

  /*
   * Profile section lookup
   */
  const sectionLookup = useMemo(
    () =>
      new Map(
        profileSections.map((section) => [section.key, section] as const),
      ),
    [profileSections],
  );

  const getSectionCount = useCallback(
    (section?: ProfileSectionConfig): number =>
      Math.max(
        0,
        Math.round(
          parseNumericValue(section?.totalCount) ??
            section?.profiles.length ??
            0,
        ),
      ),
    [],
  );

  /*
   * Website setup task navigation
   */
  const navigateWebsite = useCallback(
    (taskKey: string) => {
      const targetRoute =
        WEBSITE_ROUTES[taskKey as keyof typeof WEBSITE_ROUTES];

      if (!targetRoute) {
        console.warn("No website route found for:", taskKey);
        return;
      }

      navigate(targetRoute);
    },
    [navigate],
  );

  /*
   * Dashboard profile groups
   */
  const groupedSections = useMemo<DashboardGroupedSection[]>(
    () => [
      {
        key: "private-profiles",
        title: "Private Profiles",
        singleSection: sectionLookup.get("private-profiles"),
      },
      {
        key: "new-profiles",
        title: t("New Profiles"),
        maleSection: sectionLookup.get("my-male"),
        femaleSection: sectionLookup.get("my-female"),
      },
      {
        key: "callback-profiles",
        title: t("Call Back Profiles"),
        singleSection: sectionLookup.get("callback-profiles"),
      },
      {
        key: "not-responding-profiles",
        title: t("Not Responding Profiles"),
        singleSection: sectionLookup.get("not-responding-profiles"),
      },
      {
        key: "interested-profiles",
        title: t("Interested Profiles For Visiting"),
        singleSection: sectionLookup.get("free-profiles"),
      },
      {
        key: "paid-profiles",
        title: t("Paid Profiles"),
        singleSection: sectionLookup.get("paid-profiles"),
      },
      {
        key: "red-zone-profiles",
        title: t("Red Zone Profiles"),
        singleSection: sectionLookup.get("redzone-profiles"),
      },
      {
        key: "incomplete-profiles",
        title: t("Incomplete Profiles"),
        maleSection: sectionLookup.get("pending-male"),
        femaleSection: sectionLookup.get("pending-female"),
      },
      {
        key: "other-bureau-profiles",
        title: t("Other Bureau Profiles"),
        maleSection: sectionLookup.get("other-male"),
        femaleSection: sectionLookup.get("other-female"),
      },
      {
        key: "other-mediator-profiles",
        title: t("You added by Other Mediator Profiles"),
        singleSection: sectionLookup.get("other-mediator"),
      },
      {
        key: "shortlisted-profiles",
        title: t("Shortlisted Profiles"),
        singleSection: sectionLookup.get("shortlisted"),
      },
    ],
    [sectionLookup, t],
  );

  /*
   * Top dashboard counters
   */
  const summaryCards = useMemo(
    () => [
      {
        key: "new",
        label: t("New"),
        value:
          getSectionCount(sectionLookup.get("my-male")) +
          getSectionCount(sectionLookup.get("my-female")),
        accentClass: "bg-blue-50 text-blue-600",
        icon: <UserRound size={18} />,
      },
      {
        key: "callback",
        label: t("Call Back"),
        value: getSectionCount(sectionLookup.get("callback-profiles")),
        accentClass: "bg-orange-50 text-orange-500",
        icon: <PhoneCall size={18} />,
      },
      {
        key: "notresponding",
        label: t("Not Resp"),
        value: getSectionCount(sectionLookup.get("not-responding-profiles")),
        accentClass: "bg-rose-50 text-rose-500",
        icon: <AlertCircle size={18} />,
      },
      {
        key: "interested",
        label: t("Interested"),
        value: getSectionCount(sectionLookup.get("free-profiles")),
        accentClass: "bg-violet-50 text-violet-600",
        icon: <HeartHandshake size={18} />,
      },
      {
        key: "paid",
        label: t("Paid"),
        value: getSectionCount(sectionLookup.get("paid-profiles")),
        accentClass: "bg-emerald-50 text-emerald-600",
        icon: <BadgeIndianRupee size={18} />,
      },
      {
        key: "redzone",
        label: t("Red Zone"),
        value: getSectionCount(sectionLookup.get("redzone-profiles")),
        accentClass: "bg-red-50 text-red-500",
        icon: <ShieldAlert size={18} />,
      },
    ],
    [getSectionCount, sectionLookup, t],
  );

  const handleSummaryCardClick = useCallback(
    (cardKey: string) => {
      const newProfilesGender = sectionTabs["new-profiles"] ?? "male";

      const routeMap: Record<string, string | undefined> = {
        new:
          newProfilesGender === "female"
            ? sectionLookup.get("my-female")?.viewAllPath
            : sectionLookup.get("my-male")?.viewAllPath,

        callback: sectionLookup.get("callback-profiles")?.viewAllPath,

        notresponding: sectionLookup.get("not-responding-profiles")
          ?.viewAllPath,

        interested: sectionLookup.get("free-profiles")?.viewAllPath,

        paid: sectionLookup.get("paid-profiles")?.viewAllPath,

        redzone: sectionLookup.get("redzone-profiles")?.viewAllPath,
      };

      const targetPath = routeMap[cardKey];

      if (!targetPath) {
        return;
      }

      navigate(targetPath);
    },
    [navigate, sectionLookup, sectionTabs],
  );

  /*
   * Profile actions
   */
  const handleViewProfile = useCallback(
    (profile: ProfileCard) => {
      const profileIdentifier =
        profile.maritalId ?? profile.id ?? profile.recordId;

      if (!profileIdentifier) {
        toast.error("Unable to determine profile ID.");
        return;
      }

      persistDashboardScrollState();

      navigate(
        `/admin/profile/detail/${encodeURIComponent(
          String(profileIdentifier),
        )}`,
      );
    },
    [navigate, persistDashboardScrollState],
  );

  const handleCallProfile = useCallback((profile: ProfileCard) => {
    const mobileNumber = String(profile.mobileNumber || "").trim();

    if (!mobileNumber) {
      toast.error("Mobile number not available.");
      return;
    }

    window.location.href = `tel:${mobileNumber}`;
  }, []);

  const handleShareProfile = useCallback(async (profile: ProfileCard) => {
    const profileIdentifier =
      profile.maritalId ?? profile.id ?? profile.recordId;

    if (!profileIdentifier) {
      toast.error("Unable to determine profile ID.");
      return;
    }

    const profileUrl = `${
      window.location.origin
    }/admin/profile/detail/${encodeURIComponent(String(profileIdentifier))}`;

    const shareText = `${profile.name || "Profile"}${
      profile.maritalId ? ` • ${profile.maritalId}` : ""
    }`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: profile.name || "MeeMarriage Profile",
          text: shareText,
          url: profileUrl,
        });

        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileUrl);
        toast.success("Profile link copied.");
        return;
      }

      toast.info(profileUrl);
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return;
      }

      console.error("Profile sharing failed:", error);
      toast.error("Unable to share profile.");
    }
  }, []);

  const getSectionBadge = useCallback(
    (sectionKey: string) => {
      const badge = SECTION_BADGES[sectionKey];

      if (!badge) {
        return {
          label: "",
          className: "bg-violet-600",
        };
      }

      return {
        label: t(badge.label),
        className: badge.className,
      };
    },
    [t],
  );

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return;

    void (async () => {
      await StatusBar.setOverlaysWebView({
        overlay: false,
      }).catch(() => {});

      await StatusBar.setBackgroundColor({
        color: "#C25BD8",
      }).catch(() => {});

      await StatusBar.setStyle({
        style: Style.Dark, // White icons
      }).catch(() => {});
    })();

    return () => {
      void (async () => {
        await StatusBar.setBackgroundColor({
          color: "#FFFFFFFF",
        }).catch(() => {});

        await StatusBar.setStyle({
          style: Style.Light, // Dark icons on white background
        }).catch(() => {});
      })();
    };
  }, []);

  const renderProfileGroup = (group: DashboardGroupedSection) => {
    const hasGenderSplit = Boolean(group.maleSection || group.femaleSection);

    const maleCount = getSectionCount(group.maleSection);

    const femaleCount = getSectionCount(group.femaleSection);

    const activeTab = sectionTabs[group.key] ?? "male";

    const activeSection = hasGenderSplit
      ? activeTab === "female"
        ? (group.femaleSection ?? group.maleSection)
        : (group.maleSection ?? group.femaleSection)
      : group.singleSection;

    const totalCount = hasGenderSplit
      ? maleCount + femaleCount
      : getSectionCount(group.singleSection);

    if (!activeSection || totalCount <= 0) {
      return null;
    }

    const sectionBadge = getSectionBadge(activeSection.key);

    return (
      <DashboardSection
        key={group.key}
        sectionKey={group.key}
        title={group.title}
        totalCount={totalCount}
        activeProfiles={activeSection.profiles}
        activeSection={activeSection}
        viewAllLabel={t("dashboard.buttons.viewAll")}
        emptyLabel={t("dashboard.noProfiles")}
        scoreLabel={t("Profile Score")}
        callLabel={t("Call")}
        viewLabel={t("View")}
        shareLabel={t("Share")}
        showActions={false}
        layout="scroll"
        onViewAll={() => {
          const targetPath = activeSection.viewAllPath;

          if (!targetPath) {
            return;
          }

          persistDashboardScrollState(group.key);

          navigate(targetPath);
        }}
        onViewProfile={handleViewProfile}
        onShareProfile={handleShareProfile}
        onCallProfile={handleCallProfile}
        renderExtraActions={(profile) =>
          renderPaymentActions(activeSection.key, profile)
        }
        badgeLabel={sectionBadge.label}
        badgeClassName={sectionBadge.className}
        genderTabs={
          hasGenderSplit
            ? {
                activeTab,
                maleCount,
                femaleCount,
                onChange: (value) =>
                  setSectionTabs((previous) => ({
                    ...previous,
                    [group.key]: value,
                  })),
              }
            : undefined
        }
      />
    );
  };

  if (showInitialDashboardLoader) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        <FullPageLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FAF9F6]">
      <div className="relative">
        <div
          className="
            pointer-events-none
            absolute
            inset-x-0
            top-0
            h-[180px]
            bg-[#C062D4]
            md:hidden
          "
        />

        <div
          ref={mobileNavSpacerRef}
          className="relative pt-[var(--dashboard-nav-h,88px)] md:pt-0"
        >
          <Navbar
            onRefreshDashboard={refreshDashboard}
            dashboardRefreshing={dashboardLoading}
          />

          {isBureauSuspended ? (
            <div className="bg-red-600 text-white shadow-inner">
              <div className="mx-auto max-w-7xl px-4 py-3">
                <p className="text-sm font-semibold sm:text-base">
                  {t("alerts.suspended.accountTitle")}
                </p>

                <p className="mt-1 text-xs text-red-100 sm:text-sm">
                  {t("alerts.suspended.accountDescription")}
                </p>
              </div>
            </div>
          ) : null}

          <main className="mx-auto max-w-7xl space-y-2 px-2 sm:px-4 py-2 pb-32 sm:py-2 md:pb-8">
            {!isBureauSuspended && <WalletPanel />}

            <BusinessScore
              brandValueDisplay={businessScore.brandValueDisplay}
              totalRegisteredProfiles={businessScore.totalRegisteredProfiles}
              brandValueProgressWidth={businessScore.brandValueProgressWidth}
            />

            <DashboardStatCard
              handleSummaryCardClick={handleSummaryCardClick}
              summaryCards={summaryCards}
            />

            {dashboardData && dashboardData.websiteStatus !== "complete" ? (
              <div className="hidden rounded-2xl border border-yellow-200 bg-yellow-50 p-4 shadow-sm sm:block">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-yellow-900 sm:text-base">
                      {t("Your customer website is pending.")}
                    </p>

                    <p className="mt-1 text-xs text-yellow-700 sm:text-sm">
                      {t(
                        "Complete your website setup to improve customer trust.",
                      )}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const bureauId = bureauInfo?.bureauId;
                      const bureauName = bureauInfo?.bureauName || "";

                      if (!bureauId) {
                        toast.error("Bureau ID is not available.");
                        return;
                      }

                      window.open(
                        `https://matrimonystudio.in/${encodeURIComponent(
                          String(bureauId),
                        )}/${encodeURIComponent(bureauName)}`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                  >
                    {t("Preview Website")}
                  </button>
                </div>
              </div>
            ) : null}

            {Array.isArray(dashboardData?.websiteTasks) &&
            dashboardData.websiteTasks.length > 0 ? (
              <section className="hidden p-2 shadow-sm sm:block">
                <h2 className="text-base font-semibold text-slate-900">
                  {t("Complete Your Website Setup")}
                </h2>

                <div className="-mx-4 mt-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <div className="flex gap-3">
                    {dashboardData.websiteTasks.map((task: any) => (
                      <button
                        key={String(task.key)}
                        type="button"
                        onClick={() => navigateWebsite(String(task.key))}
                        className="min-w-[220px] rounded-2xl border border-violet-100 bg-[linear-gradient(135deg,#f5f3ff_0%,#fff1f2_100%)] p-4 text-left shadow-sm"
                      >
                        <p className="text-sm font-semibold text-slate-900">
                          {t(task.label)}
                        </p>

                        <p className="mt-2 text-xs text-slate-600">
                          {t(task.description)}
                        </p>

                        <span className="mt-4 inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-violet-700">
                          {task.completed
                            ? `${t("Completed")} ✓`
                            : t("Pending")}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}

            {dashboardError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 sm:text-base">
                {dashboardError}
              </div>
            ) : null}

            {superAdminProfiles.length > 0 ? (
              <DirectRegisteredProfiles
                profiles={superAdminProfiles}
                totalCount={superAdminProfilesTotal}
                scrollerRef={superAdminScrollerRef}
                title={t("Direct Registered Profiles")}
                takeProfileLabel={t("Take This Profile")}
                onViewProfile={handleViewProfile}
                onTakeProfile={openWhatsAppForSuperAdminProfile}
                onViewAll={() => navigate("/admin/profiles/all/direct")}
                viewAllLabel={t("View All")}
              />
            ) : null}

            <section className="">
              {groupedSections.map(renderProfileGroup)}
            </section>
          </main>

          {isWarningModalOpen ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                <h2 className="mb-3 text-lg font-semibold text-slate-900">
                  {t("Notice")}
                </h2>

                <p className="text-sm text-slate-600">
                  {t("contact.details.warning")}
                </p>

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsWarningModalOpen(false);
                      navigate("/admin/user/profile/own");
                    }}
                    className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white"
                  >
                    {t("bottomNav.addProfile")}
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsWarningModalOpen(false)}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                  >
                    {t("skip")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <BottomNavbar />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
